import { useReducer, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useHover } from 'react-use';

import enhancementPackages from '../../helpers/enhancementPackages';
import DisplayEnhancementPackage from '../../components/actions/DisplayEnhancementPackage';
import Button from '../../components/inputs/Button';
import {
  initialState,
  reducer,
  START_ACTION,
  CLEAR_SUCCESS,
} from '../../reducers/actionReducer';
import ActionModal from '../../components/ActionModal';
import ToastNotification from '../../components/ToastNotification';
import { FILES_QUERY } from '../../queries';
import { DEFAULT_ENHANCEMENT_PACKAGES_QUERY } from '../../queries/packageQueries';
import { isIdFile } from '../../helpers/fileKinds';
import LoadingSpinner from '../../components/LoadingSpinner';
import { currency } from '../../utils';
import { useAuth } from '../../hooks/useAuth';
import { calculateEnhancementPackageRate } from '../../helpers/pricing';
import classNames from 'classnames';
import AudienceIcon from '../../components/icons/AudienceIcon';
import { isEnhancementEnabled } from '../../utils/featureFlags';
import PermissionModal from '../../components/PermissionModal';

export default function Packages() {
  const isEnhancementFeatureEnabled = isEnhancementEnabled();
  const [actionState, dispatchAction] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { fileId } = useParams();
  const [matchedEnhancement, setMatchedEnhancement] = useState(null);

  const { loading, error, data } = useQuery(FILES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const files = data?.files;

  // Fetch default enhancement packages from backend
  const { data: defaultPkgsData } = useQuery(DEFAULT_ENHANCEMENT_PACKAGES_QUERY, {
    fetchPolicy: 'cache-first',
  });

  // Create alias map for package name normalization
  const aliasMap = { Foundation: 'Foundations' };

  // Merge local helper data with backend variables
  const mergedPackages = enhancementPackages.map((pkg) => {
    // Handle "Foundation" -> "Foundations" aliasing
    const backendPackageName = aliasMap[pkg.packageName] || pkg.packageName;
    const backendPkg = defaultPkgsData?.defaultEnhancementPackages?.find(
      (p: any) => p?.packageName === backendPackageName
    );

    // Merge backend variables with local package
    return {
      ...pkg,
      variablesIncluded: backendPkg?.variablesIncluded ?? pkg.variablesIncluded,
    };
  });

  useEffect(() => {
    if (fileId) {
      function navigateToMyWorkFiles() {
        navigate('../../mywork/files');
      }
      if (error) {
        navigateToMyWorkFiles();
      }
      if (!loading && files && files?.length) {
        // ATAD-136/ATAD-146: ZIP code and model files have no matched
        // records to use here.
        const foundInFiles = files.find(
          (file) => fileId === file.id && isIdFile(file)
        );
        if (!foundInFiles) {
          navigateToMyWorkFiles();
        }
        setMatchedEnhancement(foundInFiles);
      }
    }
  }, [error, fileId, loading, navigate]);

  useEffect(() => {
    if (actionState.success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [actionState.success]);

  return (
    <>
      {loading && (
        <div className="w-full h-full flex justify-center items-center">
          <LoadingSpinner message="Loading your enhancement..." />
        </div>
      )}
      {error && <Navigate to="../../../mywork/enhancements" />}
      {!loading && !error && (
        <div className="w-full 2xl:w-10/12 h-full flex flex-col px-5 pt-5">
          <h3 className="mb-2">Enhancement Packages</h3>
          <div className="flex justify-between">
            <div className="flex flex-col flex-1">
              <p className="pb-2">
                Select the ideal package of elements to append to your customer
                or prospect file to unlock personalization, segmentation, and
                new methods of outreach.
              </p>
            </div>
            <div className="flex flex-col flex-1 justify-end items-end">
              {matchedEnhancement && (
                <div className="flex flex-col">
                  <p>
                    File to enhance:{' '}
                    <span className="font-bold">{matchedEnhancement.name}</span>
                  </p>
                  <p>
                    Records matched:{' '}
                    <span className="font-bold">
                      {matchedEnhancement?.numMatchedRecords?.toLocaleString()}
                    </span>
                  </p>
                </div>
              )}
              {!matchedEnhancement && (
                <Button
                  type="button"
                  className="max-w-max"
                  primary
                  onClick={() => navigate('../fileselection')}
                >
                  Select File to Enhance
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 grid-rows-3 gap-3 2xl:gap-5 py-3 2xl-py-5">
            {mergedPackages.map((enhancementPackage) => (
              <PackageTile
                {...enhancementPackage}
                matchedEnhancement={matchedEnhancement}
                dispatchAction={dispatchAction}
                key={enhancementPackage.packageName}
                plan={user?.plan}
              />
            ))}
            <PackageTile
              matchedEnhancement={matchedEnhancement}
              dispatchAction={dispatchAction}
              packageName="Custom"
              rate={null}
              descriptionShort="Build your own enhancement package"
              descriptionLong="Select the elements you want to append to your customer or prospect file to unlock personalization, segmentation, and new methods of outreach."
              variablesIncluded={[]}
              componentType="custom"
              icon={<AudienceIcon />}
              plan={user?.plan}
              onClick={() => navigate('../custom')}
            />
          </div>
          {actionState.inProgress && (
            <ActionModal
              actionState={actionState}
              dispatchAction={dispatchAction}
              show={actionState.inProgress}
            />
          )}
          <ToastNotification
            message={actionState.success}
            success={true}
            show={actionState.success}
          />
        </div>
      )}
      {!isEnhancementFeatureEnabled && <PermissionModal module="Enhancement" />}
    </>
  );
}

const transitionClasses = 'transition-all duration-100';

type PackageTileProps = {
  packageName: string;
  productKey?: string;
  rate: number;
  descriptionShort: string;
  descriptionLong: string;
  variablesIncluded: string[];
  additionalDescription?: JSX.Element;
  includesCorePackage?: boolean;
  componentType: string;
  matchedEnhancement?: any;
  dispatchAction: any;
  icon: JSX.Element;
  plan: string;
  onClick?: () => void;
};

function PackageTile({
  packageName,
  productKey,
  rate,
  descriptionShort,
  descriptionLong,
  variablesIncluded,
  additionalDescription,
  componentType,
  matchedEnhancement,
  dispatchAction,
  icon,
  plan,
  onClick,
}: PackageTileProps) {
  function handlePackageClick(packageName) {
    // Determine if we should show two buttons (no context)
    const showTwoButtons = !matchedEnhancement;

    dispatchAction({
      type: START_ACTION,
      payload: {
        name: packageName,
        continueLabel:
          componentType === 'custom'
            ? 'Submit'
            : matchedEnhancement
            ? 'Select Package'
            : showTwoButtons
            ? 'Continue'
            : 'Select File to Enhance',
        noContinue: showTwoButtons,
        component: (
          <DisplayEnhancementPackage
            packageName={packageName}
            productKey={productKey}
            descriptionLong={descriptionLong}
            variablesIncluded={variablesIncluded}
            additionalDescription={additionalDescription}
            componentType={componentType}
            matchedEnhancement={matchedEnhancement}
            dispatchAction={dispatchAction}
            rate={calculateEnhancementPackageRate(rate, plan)}
            openOrder={undefined}
          />
        ),
      },
    });
  }

  const element = (hovered) => (
    <div
      className={`flex flex-col justify-between flex-shrink rounded-half bg-gray-100 p-5 cursor-pointer filter hover:drop-shadow-xl hover:bg-gray-200 ${transitionClasses}`}
      onClick={() =>
        typeof onClick === 'function'
          ? onClick()
          : handlePackageClick(packageName)
      }
    >
      <div>
        <div
          className={classNames(
            'mb-3 h-3 2xl:h-auto flex items-center gap-1',
            hovered && 'text-secondary',
            !hovered && 'text-primary'
          )}
        >
          {icon}
        </div>
        <h4 className="mb-1 2xl:mb-3 text-lg 2xl:text-xl">{packageName}</h4>
      </div>
      {!matchedEnhancement && (
        <p className="text-sm 2xl:text-base">{descriptionShort}</p>
      )}
      {matchedEnhancement && (
        <div className="flex flex-col space-y-2">
          {packageName === 'Custom' && (
            <>
              <p>{descriptionShort}</p>
              <p>Custom Package Pricing</p>
            </>
          )}
          {packageName !== 'Custom' && (
            <>
              {rate != null && (
                <p className={`${matchedEnhancement ? '' : 'font-bold'} mt-2`}>
                  {`$${calculateEnhancementPackageRate(
                    rate,
                    plan
                  )?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 7,
                  })}`}{' '}
                  / record
                </p>
              )}
              {matchedEnhancement && (
                <>
                  <p>
                    {matchedEnhancement.numMatchedRecords?.toLocaleString()}{' '}
                    Matched Records
                  </p>
                  {
                    <p>
                      {currency(
                        calculateEnhancementPackageRate(rate, plan) *
                          matchedEnhancement.numMatchedRecords
                      )}{' '}
                      Package Price
                    </p>
                  }
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  const [hoverable] = useHover(element);

  return hoverable;
}
